import type {
  FamilyRelation,
  KnowledgeEntry,
  KnowledgeFamilyLinkBody,
  KnowledgeKind,
  KnowledgeLinkBody,
  KnowledgeLinkDirection,
  OrgRelation,
} from '@ni/shared';
import { orgRelationSentence } from './copy';

/**
 * **The arithmetic behind "Link to another record"** (organisation-profile note
 * §1.1), kept out of the modal.
 *
 * The SPA's suite has no DOM, so anything that is only true inside a React tree
 * is not true anywhere a test can see it. What is worth pinning about this
 * gesture is not that a button calls something — it is **the direction**: a body
 * built the wrong way round would record the University of Texas as a campus of
 * its own Austin campus, and the page would say nothing about it.
 */

/**
 * **One thing the office can say about these two records**, as the fact's own
 * two words: which relation, and which slot *this* page's record sits in.
 *
 * A reading rather than a relation, because a relation on its own is only half
 * an answer between two organisations — "part of" does not say which is the
 * campus. Naming the whole sentence carries both halves at once, which is the
 * only way a reader can be sure which way round they have said it.
 *
 * The inbox's likely-merge card asks the same question the same way, off the
 * same list: it reads {@link linkReadings} for its own pair and turns each
 * reading's direction into the `whole_entity_id` its contract takes
 * (`needs-action.tsx`, `mergeCardReadings`). It used to ask in two parts —
 * which of the two is the whole, then what the other is to it — and the reader
 * had to put a direction and a verb back together from two lit buttons.
 */
export interface LinkReading {
  relation: OrgRelation;
  /** Which slot the page's own record occupies — the wire's own word. */
  direction: KnowledgeLinkDirection;
}

/**
 * **Every sentence these two records can stand in**, in the order they are
 * offered — and an empty list when there is none.
 *
 * The catalog's slot rule read as readings (`fact-types.ts`): three relations
 * between two organisations, and `hosted` between an organisation and the event
 * it put on. So:
 *
 *  - **two organisations** give five readings — containment and governance each
 *    turn round, and "affiliated" is the one relation with no direction to it,
 *    so it is offered once;
 *  - **an organisation and an event** give exactly one, whichever page it is
 *    asked from: somebody hosted it;
 *  - **a person on either side gives none**, and so do two events. There is no
 *    such fact to write, and offering a reading whose only outcome is the
 *    server's refusal is offering nothing.
 *
 * The list is what the server will accept, said once on this side so the office
 * is never shown a sentence that cannot be recorded.
 */
export function linkReadings(here: KnowledgeKind, other: KnowledgeKind): LinkReading[] {
  if (here === 'org' && other === 'org') {
    return [
      { relation: 'part_of', direction: 'subject' },
      { relation: 'part_of', direction: 'counterparty' },
      { relation: 'governed_by', direction: 'subject' },
      { relation: 'governed_by', direction: 'counterparty' },
      { relation: 'affiliated_with', direction: 'subject' },
    ];
  }
  if (here === 'org' && other === 'event') {
    return [
      { relation: 'hosted', direction: 'subject' },
      { relation: 'sponsored', direction: 'subject' },
    ];
  }
  if (here === 'event' && other === 'org') {
    return [
      { relation: 'hosted', direction: 'counterparty' },
      { relation: 'sponsored', direction: 'counterparty' },
    ];
  }
  return [];
}

/**
 * **What the record will say**, in names, in *this page's* order — and the same
 * string the server answers with, so what the modal promised before the press
 * and what it reports after it are one sentence.
 *
 * `orgRelationSentence` takes the fact's two *columns* in order, never a
 * subject-verb-object reading, so the direction is applied here: the page's own
 * record is the subject on `subject` and the counterparty on `counterparty`.
 *
 * On `affiliated_with` the stored fact may name the two the other way round —
 * its slots do not rank, so the server folds the pair onto one order for the
 * record (`knowledge-links.service.ts`, `resolvePair`) — and nothing is folded
 * here on purpose. Both readings are the one tie; the office is shown the one it
 * said.
 */
export function linkSentence(reading: LinkReading, here: string, other: string): string {
  return reading.direction === 'subject'
    ? orgRelationSentence(reading.relation, here, other)
    : orgRelationSentence(reading.relation, other, here);
}

/**
 * **What goes on the wire.** The page's own record is in the path, so the body
 * is the other one plus the reading — which is the whole reason the contract
 * takes a direction rather than two ids: one of the two is never the client's to
 * choose.
 */
export function linkBody(reading: LinkReading, otherEntityId: string): KnowledgeLinkBody {
  return {
    other_entity_id: otherEntityId,
    relation: reading.relation,
    direction: reading.direction,
  };
}

/** Two readings are the same reading when both of their words are. */
export const sameReading = (one: LinkReading, other: LinkReading): boolean =>
  one.relation === other.relation && one.direction === other.direction;

/** A reading's key, for a list of buttons that has no id of its own. */
export const readingKey = (reading: LinkReading): string =>
  `${reading.relation}:${reading.direction}`;

/**
 * **What the search may offer**, which is never a person.
 *
 * Every relation this door writes stands between organisations, or between an
 * organisation and an event. A person the office wants tied to something goes in
 * through the record's own doors — this one would only ever refuse them.
 */
export const LINK_SEARCH_KINDS: readonly KnowledgeKind[] = ['org', 'event'];

/**
 * **From a person's panel the door asks a different question**: how another
 * person is related to this one. So it searches people only, and offers the
 * family words rather than the organization sentences.
 */
export const isFamilyLink = (here: KnowledgeKind): boolean => here === 'person';

export const linkSearchKinds = (here: KnowledgeKind): readonly KnowledgeKind[] =>
  isFamilyLink(here) ? ['person'] : LINK_SEARCH_KINDS;

/** What goes on the wire for a family tie: the other person and what they are to this one. */
export function familyLinkBody(
  relation: FamilyRelation,
  otherEntityId: string,
): KnowledgeFamilyLinkBody {
  return { other_entity_id: otherEntityId, relation };
}

/**
 * The results as the modal lists them: the page's own record taken out, because
 * a record cannot be linked to itself and the server refuses it — the office
 * should not be able to pick it in the first place.
 */
export function linkCandidates(
  entries: readonly KnowledgeEntry[],
  hereEntityId: string,
): KnowledgeEntry[] {
  return entries.filter((entry) => entry.entity_id !== hereEntityId);
}
